import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Check, X, ArrowLeft, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LOGO =
  "https://customer-assets-v7afamib.emergentagent.net/job_edju-command/artifacts/tmtoc27k_edju-cat-type-logo-png.png";

const RULES = [
  { key: "len", label: "At least 12 characters", test: (p) => p.length >= 12 },
  { key: "upper", label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "num", label: "One number", test: (p) => /[0-9]/.test(p) },
  { key: "special", label: "One special character", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function formatError(detail, fallback) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ") || fallback;
  return fallback;
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | valid | invalid
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    api.validateResetToken(token)
      .then((r) => setStatus(r.valid ? "valid" : "invalid"))
      .catch(() => setStatus("invalid"));
  }, [token]);

  const rulesPass = RULES.every((r) => r.test(next));
  const matches = next.length > 0 && next === confirm;
  const canSubmit = rulesPass && matches && !busy;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!matches) return setError("New password and confirmation do not match");
    setBusy(true);
    try {
      await api.resetPassword(token, next);
      toast.success("Password updated successfully");
      setStatus("done");
      setTimeout(() => navigate("/login", { replace: true }), 3500);
    } catch (err) {
      if (err.response?.status === 400) setStatus("invalid");
      setError(formatError(err.response?.data?.detail, "Unable to reset password. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F4F6FB] px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full max-w-md"
      >
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="rounded-2xl bg-[#14245F] px-5 py-3">
            <img src={LOGO} alt="Edju Cat" className="h-8 w-auto" />
          </div>
          <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.22em] text-[#1D4ED8]">War Room</p>
        </div>

        <div className="rounded-3xl border border-[#E4E9F3] bg-white p-7 shadow-[0_10px_40px_rgba(20,36,95,0.10)]">
          {status === "checking" && (
            <div className="flex flex-col items-center py-6 text-[#5A6B8C]">
              <Loader2 size={22} className="mb-2 animate-spin text-[#1D4ED8]" /> Checking your reset link…
            </div>
          )}

          {status === "invalid" && (
            <div data-testid="reset-invalid" className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#DC2626]/10">
                <AlertTriangle size={24} className="text-[#DC2626]" />
              </div>
              <h1 className="font-heading text-xl font-bold text-[#14245F]">Link expired or invalid</h1>
              <p className="mt-2 text-sm text-[#5A6B8C]">This password reset link is no longer valid. Please request a new one.</p>
              <a href="/forgot-password" data-testid="request-new-link" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1D4ED8] hover:text-[#14245F]">
                Request a new link
              </a>
            </div>
          )}

          {status === "done" && (
            <div data-testid="reset-success" className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#0D9488]/10">
                <CheckCircle2 size={26} className="text-[#0D9488]" />
              </div>
              <h1 className="font-heading text-xl font-bold text-[#14245F]">Password updated successfully</h1>
              <p className="mt-2 text-sm text-[#5A6B8C]">You can now sign in with your new password. Taking you to sign in…</p>
              <Button onClick={() => navigate("/login", { replace: true })} data-testid="reset-goto-login" className="mt-6 w-full rounded-full bg-[#1D4ED8] py-5 font-bold text-white hover:bg-[#1A45C0]">
                Go to sign in
              </Button>
            </div>
          )}

          {status === "valid" && (
            <form onSubmit={submit} data-testid="reset-form">
              <div className="mb-6 flex items-center gap-2 text-sm font-semibold text-[#5A6B8C]">
                <ShieldCheck size={15} className="text-[#1D4ED8]" />
                <span>Choose a new password</span>
              </div>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="new" className="text-xs font-semibold text-[#14245F]">New password</Label>
                  <Input id="new" type="password" data-testid="reset-new-password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" required className="border-[#E4E9F3] bg-[#F7F9FE] focus-visible:ring-[#1D4ED8]" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirm" className="text-xs font-semibold text-[#14245F]">Confirm new password</Label>
                  <Input id="confirm" type="password" data-testid="reset-confirm-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required className="border-[#E4E9F3] bg-[#F7F9FE] focus-visible:ring-[#1D4ED8]" />
                </div>
              </div>

              <ul className="mt-4 space-y-1.5" data-testid="reset-rules">
                {RULES.map((r) => {
                  const ok = r.test(next);
                  return (
                    <li key={r.key} className={`flex items-center gap-2 text-xs ${ok ? "text-[#0D9488]" : "text-[#94A3C4]"}`}>
                      {ok ? <Check size={13} /> : <X size={13} />} {r.label}
                    </li>
                  );
                })}
                <li className={`flex items-center gap-2 text-xs ${matches ? "text-[#0D9488]" : "text-[#94A3C4]"}`}>
                  {matches ? <Check size={13} /> : <X size={13} />} Passwords match
                </li>
              </ul>

              {error && (
                <p data-testid="reset-error" className="mt-4 rounded-xl border border-[#FB6F5A]/30 bg-[#FB6F5A]/10 px-3 py-2 text-sm font-medium text-[#C2410C]">
                  {error}
                </p>
              )}

              <Button type="submit" data-testid="reset-submit" disabled={!canSubmit} className="mt-6 w-full rounded-full bg-[#1D4ED8] py-5 font-bold text-white hover:bg-[#1A45C0] disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : "Update password"}
              </Button>
              <div className="mt-4 text-center">
                <a href="/login" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1D4ED8] hover:text-[#14245F]">
                  <ArrowLeft size={13} /> Back to sign in
                </a>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
